import React, { useEffect, useCallback, useMemo } from 'react';
import { StyleSheet, TextProps, View, Platform, AccessibilityInfo } from 'react-native';
import { Label, Text } from 'tamagui';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, {
  withTiming,
  interpolateColor,
  useSharedValue,
  useAnimatedStyle,
  runOnJS,
  withSpring,
  withDelay,
} from 'react-native-reanimated';
import { CountUp } from 'use-count-up';

// Enhanced type definitions with better validation
interface PercentageBarProps {
  title?: string;
  height?: number;
  percentage: number | string; // Allow string for cases like "50%" or "N/A"
  completedColor?: string;
  backgroundColor?: string;
  suffix?: string;
  divider?: string;
  prefix?: string;
  style?: any;
  limit?: number;
  dataCount?: number | string;
  customTextStyle?: TextProps;
  showPercentage?: boolean;
  showCount?: boolean;
  animated?: boolean;
  duration?: number;
  accessibilityLabel?: string;
  testID?: string;
  onAnimationComplete?: () => void;
  variant?: 'default' | 'minimal' | 'detailed';
  size?: 'small' | 'medium' | 'large';
  colorScheme?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

// Color scheme configurations
const COLOR_SCHEMES = {
  primary: { start: '#3B82F6', end: '#1D4ED8' },
  success: { start: '#10B981', end: '#047857' },
  warning: { start: '#F59E0B', end: '#D97706' },
  danger: { start: '#EF4444', end: '#DC2626' },
  info: { start: '#06B6D4', end: '#0891B2' },
};

// Size configurations
const SIZE_CONFIG = {
  small: { height: 12, fontSize: 12, padding: 4 },
  medium: { height: 20, fontSize: 14, padding: 6 },
  large: { height: 28, fontSize: 16, padding: 8 },
};

const PercentageBar: React.FC<PercentageBarProps> = ({
  dataCount,
  divider = '/',
  percentage,
  suffix = '',
  prefix = '',
  limit,
  customTextStyle,
  height,
  backgroundColor,
  completedColor,
  title,
  style,
  showPercentage = true,
  showCount = true,
  animated = true,
  duration = 1000,
  accessibilityLabel,
  testID,
  onAnimationComplete,
  variant = 'default',
  size = 'medium',
  colorScheme = 'primary',
}) => {
  // Validate percentage input - handle string percentages, NaN, and invalid values
  const normalizedPercentage = useMemo(() => {
    let numPercentage: number;

    // Handle string percentages like "50%" or "N/A"
    if (typeof percentage === 'string') {
      const cleaned = percentage.replace('%', '').trim();
      numPercentage = parseFloat(cleaned);
    } else {
      numPercentage = typeof percentage === 'number' ? percentage : 0;
    }

    // Validate the number
    if (isNaN(numPercentage) || !isFinite(numPercentage)) {
      numPercentage = 0;
    }

    return Math.max(0, Math.min(100, numPercentage));
  }, [percentage]);

  // Shared animated values
  const widthAnim = useSharedValue(0);
  const opacityAnim = useSharedValue(0);

  // Get configuration based on props
  const config = useMemo(() => {
    const sizeConfig = SIZE_CONFIG[size];
    const colors = COLOR_SCHEMES[colorScheme];

    return {
      height: height || sizeConfig.height,
      fontSize: sizeConfig.fontSize,
      padding: sizeConfig.padding,
      colors,
      completedColor: completedColor || colors.end,
      backgroundColor: backgroundColor || colors.start,
    };
  }, [height, size, colorScheme, completedColor, backgroundColor]);

  // Enhanced animated style with better color interpolation
  const animatedStyle = useAnimatedStyle(() => {
    const progress = widthAnim.value;
    const color = interpolateColor(
      progress,
      [0, 30, 60, 80, 100],
      ['#10B981', '#F59E0B', '#F59E0B', config.completedColor, config.completedColor]
    );

    return {
      width: `${progress}%`,
      backgroundColor: color,
      opacity: opacityAnim.value,
    };
  }, [config.completedColor]);

  // Improved animation with spring physics
  useEffect(() => {
    if (!animated) {
      widthAnim.value = normalizedPercentage;
      opacityAnim.value = 1;
      return;
    }

    opacityAnim.value = withTiming(1, { duration: 200 });
    widthAnim.value = withDelay(
      200,
      withSpring(normalizedPercentage, {
        damping: 15,
        stiffness: 150,
        mass: 1,
      }, (finished) => {
        if (finished && onAnimationComplete) {
          runOnJS(onAnimationComplete)();
        }
      })
    );
  }, [normalizedPercentage, animated, onAnimationComplete]);

  // Enhanced title rendering with better accessibility
  const renderTitle = useCallback(() => {
    if (!title && variant === 'minimal') return null;

    return (
      <Label
        accessibilityRole="text"
        accessibilityLabel={accessibilityLabel || `${title}: ${normalizedPercentage}%`}
        style={styles.title}
      >
        {title && `${title} `}
        {showCount && dataCount !== undefined && (
          <>
            {prefix}{dataCount}{suffix} {divider} {prefix}{limit}{suffix}
          </>
        )}
      </Label>
    );
  }, [title, variant, accessibilityLabel, normalizedPercentage, showCount, dataCount, prefix, suffix, divider, limit]);

  // Enhanced progress bar with better styling
  const renderProgressBar = useCallback(() => (
    <Animated.View
      style={[
        styles.progressBar,
        {
          height: config.height,
          borderRadius: config.height / 2,
          padding: config.padding,
        },
        animatedStyle,
      ]}
      testID={testID ? `${testID}-progress` : undefined}
    >
      <LinearGradient
        colors={['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.1)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.gradientOverlay}
      />
    </Animated.View>
  ), [config, animatedStyle, testID]);

  // Floating percentage display outside the progress bar
  const renderFloatingPercentage = useCallback(() => (
    showPercentage && (
      <Text
        style={[
          styles.floatingPercentageText,
          {
            fontSize: config.fontSize + 2,
            color: config.completedColor,
            ...customTextStyle,
          },
        ]}
        numberOfLines={1}
        accessibilityLabel={`${normalizedPercentage} percent`}
      >
        <CountUp
          isCounting
          end={normalizedPercentage}
          duration={duration / 1000}
        />
        %
      </Text>
    )
  ), [showPercentage, normalizedPercentage, config.fontSize, config.completedColor, customTextStyle, duration]);

  // Generate accessibility label
  const accessibilityProps = useMemo(() => ({
    accessibilityRole: 'progressbar' as const,
    accessibilityLabel: accessibilityLabel || `${title || 'Progress'}: ${normalizedPercentage}%`,
    accessibilityValue: {
      min: 0,
      max: 100,
      now: normalizedPercentage,
    },
  }), [accessibilityLabel, title, normalizedPercentage]);

  return (
    <View style={[styles.container, style]} {...accessibilityProps} testID={testID}>
      {renderTitle()}
      <View style={styles.progressWrapper}>
        <View style={[styles.progressContainer, { height: config.height }]}>
          {renderProgressBar()}
          {renderFloatingPercentage()}
        </View>
      </View>
    </View>
  );
};

// Modern StyleSheet with better organization
const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginVertical: 4,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
    lineHeight: 20,
  },
  progressWrapper: {
    position: 'relative',
    justifyContent: 'center',
  },
  progressContainer: {
    backgroundColor: '#E5E7EB',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
  },
  progressBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    minWidth: 20,
    flex: 1,
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 12,
  },
  floatingPercentageText: {
    fontWeight: '700',
    textAlign: 'right',
    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
  },
});

export default PercentageBar;
